import TRTC, { RtcError, TRTCDeviceAction, TRTCDeviceType } from "trtc-sdk-v5";
import {
    IServiceVideoChatAvatar,
    IServiceVideoChatDevice,
    IServiceVideoChatJoinRoomConfig,
    IsSupportedResult,
    IServiceVideoChatUID,
    IServiceVideoChat,
} from "@obsbot/remo-services";
import {
    AudioVolumeChanged,
    DeviceChangeEventData,
    RemoteUserStatus,
    TencentRTCWebConfig,
} from "./typing";
import { SideEffectManager } from "side-effect-manager";
import { RTCLocalAvatar } from "./rtc-local-avatar";
import { RTCRemoteAvatar } from "./rtc-remote-avatar";

enum DeviceAvailableType {
    VIDEO,
    AUDIO,
}

const enum TRTCConstants {
    VOLUME_EVALUATION_INTERVAL = 300,
    VOLUME_CHANGE_THRESHOLD = 0.00001,
}

export class TencentRTCWeb extends IServiceVideoChat {
    private localMicId?: string;
    private localCameraId?: string;
    private localSpeakerId?: string;

    private isOpenCameraError = false;
    private openCameraErrorEl: HTMLElement | null = null;

    public playoutVolume = 100;
    public captureVolume = 100;
    public enabledListen = false;
    public notListenPlayoutVolume: number | undefined;

    // 是否开启摄像头镜像
    public isVideoMirror = true;

    public readonly rtc: TRTC;
    private readonly APPID: number;
    // 房间uid
    private roomUUID?: string;
    // 用户uid
    public uid: IServiceVideoChatUID = "";
    private _pJoiningRoom?: Promise<unknown>;
    private _pLeavingRoom?: Promise<unknown>;
    private readonly _roomSideEffect = new SideEffectManager();

    // 本地的avatar
    private _localAvatar?: RTCLocalAvatar;
    public get localAvatar(): IServiceVideoChatAvatar {
        return (this._localAvatar ??= new RTCLocalAvatar({ rtc: this }));
    }
    // 远端用户的avatar缓存
    private _remoteAvatars = new Map<IServiceVideoChatUID, RTCRemoteAvatar>();

    // 音量状态的Map, 记录每个用户的音量大小
    private _volumeLevels = new Map<IServiceVideoChatUID, number>();

    public get isJoinedRoom(): boolean {
        return Boolean(this.roomUUID);
    }

    public constructor(config: TencentRTCWebConfig) {
        super();
        this.APPID = config.appId;
        const rtc = TRTC.create();
        this.rtc = rtc;
        this._init(rtc);
    }
    private _init(rtc: TRTC): void {
        // 监听设备的变化
        this.sideEffect.add(() => {
            const onDeviceChanged = ({ action, type, device }: DeviceChangeEventData): void => {
                const emitDeviceId =
                    action === TRTCDeviceAction.Remove ? undefined : device.deviceId;
                switch (type) {
                    case TRTCDeviceType.Camera: {
                        this.events.emit("camera-changed", emitDeviceId);
                        break;
                    }
                    case TRTCDeviceType.Microphone: {
                        this.events.emit("mic-changed", emitDeviceId);
                        break;
                    }
                    case TRTCDeviceType.Speaker: {
                        this.events.emit("speaker-changed", emitDeviceId);
                        break;
                    }
                    default: {
                        break;
                    }
                }
            };
            rtc.on(TRTC.EVENT.DEVICE_CHANGED, onDeviceChanged);

            return () => {
                rtc.off(TRTC.EVENT.DEVICE_CHANGED, onDeviceChanged);
            };
        }, "init");

        // 处理报错的情况
        this.sideEffect.add(() => {
            const onError = (error: RtcError): void => {
                const entries = Object.entries(error);
                this.events.emit(
                    "error",
                    new Error(`TRTC Error [${error.functionName}]: \n ${entries.join("\n")}`),
                );
            };
            rtc.on("error", onError);
            return () => rtc.off("error", onError);
        });

        TRTC.setLogLevel(3);
    }

    public override async destroy(): Promise<void> {
        super.destroy();
        this.sideEffect.flushAll();
        await this.leaveRoom();
    }
    /**
     * @description 加入房间, 请在调用前 调用 `isSupported` 进行判断是否支持当前浏览器
     * @param {IServiceVideoChatJoinRoomConfig} config 加入房间的参数
     */
    public async joinRoom(config: IServiceVideoChatJoinRoomConfig): Promise<void> {
        if (!this.rtc) {
            throw new Error("TRTC is not provided");
        }
        if (this._pJoiningRoom) {
            await this._pJoiningRoom;
        }
        if (this._pLeavingRoom) {
            await this._pLeavingRoom;
        }
        if (this.roomUUID) {
            if (this.roomUUID === config.roomUUID) {
                return;
            }
            await this.leaveRoom();
        }

        this._pJoiningRoom = this._joinRoom(config);
        await this._pJoiningRoom;
        this._pJoiningRoom = undefined;
    }
    private async _joinRoom({
        uid,
        roomUUID,
        userSig,
        isTurnListenOn,
        isEnableAES,
        isEnableAGC,
    }: IServiceVideoChatJoinRoomConfig): Promise<void> {
        this._roomSideEffect.flushAll();
        const rtc = this.rtc;
        // 进入房间前先关闭摄像头和麦克风的采集, 防止进入房间前就打开了摄像头或麦克风
        rtc.stopLocalVideo();
        rtc.stopLocalAudio();
        this.enabledListen = isTurnListenOn ?? false;
        console.log("[tencent-rtc-web] Web查看是否开启回声抑制与自动增益", {
            isEnableAES,
            isEnableAGC,
        });
        this.setAudioAEC(isEnableAES ?? false);
        this.setAudioAGC(isEnableAGC ?? false);
        // 监听事件
        // 1. 监听远端用户加入或退出房间
        this._roomSideEffect.add(() => {
            const enterHandler = ({ userId }: RemoteUserStatus): void => {
                console.log("[tencent-rtc-web] 远端用户加入房间", userId);
                const uid: IServiceVideoChatUID = userId;
                let avatar = this._remoteAvatars.get(uid);
                if (!avatar) {
                    avatar = new RTCRemoteAvatar({ rtc: this, uid });
                    this._remoteAvatars.set(uid, avatar);
                }
                // this.events.emit("user-enter-channel", userId);
                avatar.setActive(true);
            };

            const leaveHandler = ({ userId }: RemoteUserStatus): void => {
                console.log("[tencent-rtc-web] 远端用户退出房间", userId);
                const uid: IServiceVideoChatUID = userId;
                const avatar = this._remoteAvatars.get(uid);
                if (avatar) {
                    avatar.destroy();
                    this._remoteAvatars.delete(uid);
                }
                this.events.emit("user-leave-channel", userId);
            };

            rtc.on("remote-user-enter", enterHandler);
            rtc.on("remote-user-exit", leaveHandler);
            return () => {
                rtc.off("remote-user-enter", enterHandler);
                rtc.off("remote-user-exit", leaveHandler);
            };
        });
        // 2. 监听远端用户开启video/audio
        this._roomSideEffect.add(() => {
            const handleRTCAvailable = (
                userId: string,
                available: boolean,
                type: DeviceAvailableType,
            ): void => {
                console.log("[tencent-rtc-web] 远端用户开启video/audio", type, userId, available);
                let avatar = this._remoteAvatars.get(userId);
                if (!avatar) {
                    avatar = new RTCRemoteAvatar({ rtc: this, uid: userId });
                    this._remoteAvatars.set(userId, avatar);
                    avatar.setActive(true);
                }
                if (type === DeviceAvailableType.VIDEO) {
                    console.log("[tencent-rtc-web] 远端用户开启video", userId, available);
                    avatar.enableCamera(available);
                    this.events.emit("update-state", { userID: userId, shouldCamera: available });
                } else if (type === DeviceAvailableType.AUDIO) {
                    console.log("[tencent-rtc-web] 远端用户开启audio", userId, available);
                    avatar.enableMic(available);
                    rtc.setRemoteAudioVolume(userId, this.enabledListen ? this.playoutVolume : 0);
                    this.events.emit("update-state", { userID: userId, shouldMic: available });
                }
            };
            const onDeviceAudioAvailable = ({ userId }: RemoteUserStatus): void =>
                handleRTCAvailable(userId, true, DeviceAvailableType.AUDIO);
            const onDeviceAudioUnavailable = ({ userId }: RemoteUserStatus): void =>
                handleRTCAvailable(userId, false, DeviceAvailableType.AUDIO);
            const onDeviceVideoAvailable = ({ userId }: RemoteUserStatus): void =>
                handleRTCAvailable(userId, true, DeviceAvailableType.VIDEO);
            const onDeviceVideoUnavailable = ({ userId }: RemoteUserStatus): void =>
                handleRTCAvailable(userId, false, DeviceAvailableType.VIDEO);
            rtc.on("remote-audio-available", onDeviceAudioAvailable);
            rtc.on("remote-audio-unavailable", onDeviceAudioUnavailable);
            rtc.on("remote-video-available", onDeviceVideoAvailable);
            rtc.on("remote-video-unavailable", onDeviceVideoUnavailable);

            return () => {
                rtc.off("remote-audio-available", onDeviceAudioAvailable);
                rtc.off("remote-audio-unavailable", onDeviceAudioUnavailable);
                rtc.off("remote-video-available", onDeviceVideoAvailable);
                rtc.off("remote-video-unavailable", onDeviceVideoUnavailable);
            };
        });
        // 3. 监听音量变化
        this._roomSideEffect.add(() => {
            const handleVolumeChanged = ({ result }: AudioVolumeChanged): void => {
                result.forEach(({ userId, volume }) => {
                    const showVolume = volume;
                    const oldVolume = this._volumeLevels.get(userId) || 0;
                    this._volumeLevels.set(userId, showVolume);
                    if (userId === "" || userId === this.uid) {
                        if (this.uid) {
                            this._volumeLevels.set(this.uid, showVolume);
                        }
                        if (Math.abs(oldVolume - showVolume) > 0.00001) {
                            console.log("[tencent-rtc-web] 自身音量发生改变", oldVolume);
                            this.events.emit("volume-level-changed", oldVolume);
                            this.events.emit("user-mic-level-changed", {
                                userID: userId || this.uid,
                                level: showVolume,
                            });
                        }
                    } else {
                        if (Math.abs(oldVolume - showVolume) > 0.00001) {
                            console.info(
                                `[tencent-rtc-web] 用户 [${userId}] 音量发生改变`,
                                oldVolume,
                            );
                            this.events.emit("user-mic-level-changed", {
                                userID: userId,
                                level: showVolume,
                            });
                        }
                    }
                });
            };
            rtc.enableAudioVolumeEvaluation(300);
            rtc.on("audio-volume", handleVolumeChanged);
            return () => {
                rtc.off("audio-volume", handleVolumeChanged);
            };
        });
        // 4. 监听网络质量信息, 如果需要远端用户的网络质量则需要使用IM广播出去
        // this._roomSideEffect.add(() => {
        //     const handleNetwork = (statistics: NetworkQuality): void => {
        //         this.events.emit("network", {
        //             uplinkLoss: statistics.uplinkLoss,
        //             uplinkNetworkQuality:
        //                 statistics.uplinkNetworkQuality as unknown as NetworkQualityValue,
        //             uplinkRTT: statistics.uplinkRTT,
        //             downlinkLoss: statistics.downlinkLoss,
        //             downlinkNetworkQuality:
        //                 statistics.downlinkNetworkQuality as unknown as NetworkQualityValue,
        //             downlinkRTT: statistics.downlinkRTT,
        //         });
        //     };
        //     rtc.on("network-quality", handleNetwork);
        //     return () => rtc.off("network-quality", handleNetwork);
        // });
        await rtc.enterRoom({
            userId: uid,
            userSig,
            strRoomId: String(roomUUID),
            sdkAppId: this.APPID,
            scene: TRTC.TYPE.SCENE_RTC,
            autoReceiveVideo: true,
            autoReceiveAudio: true,
        });
        console.log("[tencent-rtc-web] 加入房间成功");
        this.uid = uid;
        this.roomUUID = roomUUID;
    }
    public async leaveRoom(): Promise<void> {
        if (this.roomUUID) {
            this.rtc.exitRoom();
            this.rtc.stopLocalAudio();
            this.rtc.stopLocalVideo();
        }
        this._roomSideEffect.flushAll();
        this._remoteAvatars.clear();
        this.uid = "";
        this.roomUUID = "";
    }
    public override getAvatar(uid: IServiceVideoChatUID): IServiceVideoChatAvatar | undefined {
        if (!this.isJoinedRoom) {
            return;
        }
        if (!uid || uid === "" || this.uid === uid) {
            return this.localAvatar;
        }
        let remoteAvatar = this._remoteAvatars.get(uid);
        if (!remoteAvatar) {
            remoteAvatar = new RTCRemoteAvatar({ rtc: this, uid });
            this._remoteAvatars.set(uid, remoteAvatar);
        }
        return remoteAvatar;
    }
    public override getVolumeLevel(uid?: IServiceVideoChatUID): number {
        if (!uid) {
            uid = this.uid;
        }
        return this._volumeLevels.get(uid || "") || 0;
    }
    public override getCameraDevices(): Promise<IServiceVideoChatDevice[]> {
        return TRTC.getCameraList().then(devices => {
            return devices.map(device => ({
                deviceId: device.deviceId,
                label: device.label,
            }));
        });
    }
    public override getMicDevices(): Promise<IServiceVideoChatDevice[]> {
        return TRTC.getMicrophoneList().then(devices => {
            return devices.map(device => ({
                deviceId: device.deviceId,
                label: device.label,
            }));
        });
    }
    public override getSpeakerDevices(): Promise<IServiceVideoChatDevice[]> {
        return TRTC.getSpeakerList().then(devices => {
            return devices.map(device => ({
                deviceId: device.deviceId,
                label: device.label,
            }));
        });
    }
    public async isSupported(): Promise<IsSupportedResult> {
        return TRTC.isSupported();
    }

    public override async setCameraID(deviceId: string): Promise<void> {
        this.localCameraId = deviceId;
        (this.localAvatar as any).setCameraId(deviceId);
        try {
            if (this.isOpenCameraError && this.openCameraErrorEl) {
                this.startCameraTest(this.openCameraErrorEl);
            }
            return await this.rtc.updateLocalVideo({ option: { cameraId: deviceId } });
        } catch (error) {
            if (!error.message.includes("not started")) {
                console.error(error);
            }
        }
    }
    public override getCameraID(): string | undefined {
        return this.localCameraId;
    }
    public override async setMicID(deviceId: string): Promise<void> {
        this.localMicId = deviceId;
        (this.localAvatar as any).setMicId(deviceId);
        if ((this.localAvatar as any).isStartAudio === false) {
            return;
        }
        try {
            return await this.rtc.updateLocalAudio({ option: { microphoneId: deviceId } });
        } catch (error) {
            if (!error.message.includes("update not allowed when stopping")) {
                console.error(error);
            }
        }
    }
    public override getMicID(): string | undefined {
        return this.localMicId;
    }
    public override async setSpeakerID(deviceId: string): Promise<void> {
        this.localSpeakerId = deviceId;
        return await TRTC.setCurrentSpeaker(deviceId);
    }
    public override getSpeakerID(): string | undefined {
        return this.localSpeakerId;
    }

    public override startCameraTest(el: HTMLElement): void {
        const handleStartCameraTest = async (): Promise<void> => {
            try {
                const rtc = this.rtc;
                el.innerHTML = "";
                await rtc.startLocalVideo({
                    publish: false,
                    view: el,
                    mute: false,
                    option: {
                        cameraId: this.localCameraId,
                        mirror: this.isVideoMirror ? "both" : false,
                        fillMode: "cover",
                    },
                });
                this.isOpenCameraError = false;
                this.openCameraErrorEl = null;
            } catch (error) {
                this.isOpenCameraError = true;
                this.openCameraErrorEl = el;
            }
        };

        handleStartCameraTest();
    }

    public override stopCameraTest(): void {
        this.rtc.stopLocalVideo();
    }

    public override startMicTest(): void {
        const rtc = this.rtc;
        rtc.startLocalAudio({
            publish: false,
            mute: false,
            option: { microphoneId: this.localMicId },
        });
        this.sideEffect.add(() => {
            const onAudioVolumeChanged = (data: AudioVolumeChanged): void => {
                const { result } = data;
                const userSelf = result.filter(user => user.userId === "");
                console.log("[RTC] [LOG] 查看麦克风音量显示", userSelf[0]?.volume);
                if (userSelf[0]) {
                    this.events.emit("test-mic-volume-changed", userSelf[0].volume);
                }
            };
            rtc.on(TRTC.EVENT.AUDIO_VOLUME, onAudioVolumeChanged);
            return () => {
                rtc.off(TRTC.EVENT.AUDIO_VOLUME, onAudioVolumeChanged);
            };
        }, "mic-test");
        rtc.enableAudioVolumeEvaluation(TRTCConstants.VOLUME_EVALUATION_INTERVAL);
    }

    public override stopMicTest(): void {
        this.rtc.stopLocalAudio();
        this.sideEffect.flush("mic-test");
    }

    public override startSpeakerTest(_filePath: string): void {
        console.error("start speaker test is not supported");
    }

    public override stopSpeakerTest(): void {
        console.error("stop speaker test is not supported");
    }

    public override getSpeakerVolume(): number {
        return this.notListenPlayoutVolume === undefined
            ? this.playoutVolume
            : this.notListenPlayoutVolume;
    }
    public override getMicVolume(): number {
        return this.captureVolume;
    }
    public override setMicVolume(volume: number): void {
        this.captureVolume = volume;
    }
    public override setIsMuteAllRemoteStream(_enabled: boolean): void {
        console.error("setIsMuteAllRemoteStream Method not implemented.");
    }

    public override setSpeakerVolume(volume: number): void {
        this.playoutVolume = volume;
    }

    public override startNetworkTest(): void {
        console.error("start network test is not supported");
    }

    public override stopNetworkTest(): void {
        console.error("stop network test is not supported");
    }

    public override setVideoMirror(enabled: boolean): void {
        const setVideoMirrorFn = async (): Promise<void> => {
            try {
                this.isVideoMirror = enabled;
                await this.rtc.updateLocalVideo({ option: { mirror: enabled ? "both" : false } });
            } catch (error) {
                if (!error.message.includes("not started")) {
                    console.error(error);
                }
            }
        };
        setVideoMirrorFn();
    }

    public override setPlayoutVolume(volume: number): void {
        if (this.enabledListen === false) {
            this.notListenPlayoutVolume = volume;
        } else {
            this.notListenPlayoutVolume = undefined;
            this.playoutVolume = volume;
            this._remoteAvatars.forEach((_avatar, uid) => {
                this.rtc.setRemoteAudioVolume(uid, volume);
            });
        }
    }

    public setIsMuteAllRemoteAudio(enabled: boolean): void {
        this._remoteAvatars.forEach((_avatar, uid) => {
            this.rtc.setRemoteAudioVolume(uid, enabled ? this.playoutVolume : 0);
        });
    }

    public override setCaptureVolume(volume: number): void {
        this.captureVolume = volume;
        this.rtc.updateLocalAudio({
            option: {
                captureVolume: volume,
            },
        });
    }

    public override setAudioAEC(enabled: boolean): void {
        this.rtc.updateLocalAudio({
            option: {
                echoCancellation: enabled,
            },
        });
    }

    public override setAudioAGC(enabled: boolean): void {
        this.rtc.updateLocalAudio({
            option: {
                autoGainControl: enabled,
            },
        });
    }
}
